import { ORDER_STATUSES } from "@osmix/auth-policy"
import { z } from "zod"
import {
  PartnerRefSchema,
  PromoAudienceSchema,
  PromoRewardKindSchema,
  PromoStateSchema,
  PromoSummarySchema,
} from "./promo-partners"

export const DeliveryMethodSchema = z.enum(["standard", "express", "pickup"])
export type DeliveryMethod = z.infer<typeof DeliveryMethodSchema>

// «self» — своя доставка продавца («Доставляю сам»), «pecom»/«dellin» — ПЭК и
// Деловые линии (крупногабарит). Добавлены 25.09.2026 аддитивно: заказ с таким
// отправлением не должен выпадать из списков, где элементы валидируются.
// «seller_pickup» — самовывоз из точки продавца (17.10.2026), тоже аддитивно.
export const DeliveryCarrierSchema = z.enum(["sdek", "flat", "self", "pecom", "dellin", "seller_pickup"])
export type DeliveryCarrier = z.infer<typeof DeliveryCarrierSchema>

// `awaiting_payment` — счётчик «Ждут оплаты» мобильного макета (§2). Добавлен
// АДДИТИВНО на уровне контракта поверх канонического ORDER_STATUSES из
// @osmix/auth-policy: расширяем перечисление, не трогая единый источник статусов.
export const ORDER_STATUSES_EXT = [...ORDER_STATUSES, "awaiting_payment"] as const
export const OrderStatusSchema = z.enum(ORDER_STATUSES_EXT)
export type OrderStatus = z.infer<typeof OrderStatusSchema>

export const CartUnavailableReasonSchema = z.enum(["out_of_stock", "moderation", "unpublished"])
export type CartUnavailableReason = z.infer<typeof CartUnavailableReasonSchema>

// free_delivery — код обнуляет доставку групп под условиями (fd-promo-05).
export const PromoKindSchema = z.enum(["percent", "fixed", "free_delivery"])
export type PromoKind = z.infer<typeof PromoKindSchema>

export const CartItemSchema = z.object({
  id: z.string(),
  productId: z.string(),
  shopId: z.string(),
  name: z.string(),
  brand: z.string(),
  photo: z.string(),
  variantId: z.string().optional(),
  variantLabel: z.string().optional(),
  options: z.record(z.string(), z.string()).optional(),
  size: z.string(),
  color: z.string(),
  qty: z.number().int(),
  unitPrice: z.number(),
  oldUnitPrice: z.number().optional(),
  lineTotal: z.number(),
  stock: z.number().int(),
  /** Минимальный заказ, шт. (миграция 422): меньше в корзине держать нельзя. Нет - без ограничения. */
  minOrderQty: z.number().int().positive().optional(),
  /** Товар изготавливается на заказ — попадает во вкладку «Под заказ» корзины. */
  madeToOrder: z.boolean().default(false),
  /** §6: изображение товара «в вырезе» (PNG на прозрачном фоне) для коллажных
   *  блоков мобилки. Пусто/отсутствует, если у товара его нет. */
  cutoutImageUrl: z.string().optional(),
  weightGrams: z.number().int().positive().optional(),
  lengthCm: z.number().int().positive().optional(),
  widthCm: z.number().int().positive().optional(),
  heightCm: z.number().int().positive().optional(),
  available: z.boolean(),
  /** Почему позиция недоступна: товар кончился, ушёл на перемодерацию или снят
   *  с продажи. Корзина показывает недоступную позицию серой и в конце списка —
   *  и должна отличать «закончился» от «ждёт проверки». */
  unavailableReason: CartUnavailableReasonSchema.optional(),
  // ── Механики П2, аддитивно ──
  /** Доля скидки по промокоду на всю строку, рубли (fd-promo-15). */
  discount: z.number().optional(),
  /** Промокод действует на эту позицию — пометка в корзине. */
  promoApplied: z.boolean().optional(),
  /** Действующая акция магазина: цена строки уже со скидкой акции (fd-promo-03). */
  sellerPromo: z.object({ id: z.string(), title: z.string() }).optional(),
})
export type CartItem = z.infer<typeof CartItemSchema>

export const CartSchema = z.object({
  id: z.string(),
  items: z.array(CartItemSchema),
  itemCount: z.number().int(),
  subtotal: z.number(),
  /** Скидка платформы «Скидка OSMIX» в рублях (0 — промо не применено). */
  discount: z.number().default(0),
  /** Применённый промокод (например «OSMIX10») или отсутствует. */
  promoCode: z.string().optional(),
  /** Тип применённого кода; free_delivery — скидка в доставке (fd-promo-05). */
  promoKind: PromoKindSchema.optional(),
  /** Код магазина: id магазина-владельца (скидка за его счёт). */
  promoShopId: z.string().optional(),
  /** Магазины, доставка которых бесплатна по коду. */
  freeDeliveryShopIds: z.array(z.string()).optional(),
  updatedAt: z.string(),
})
export type Cart = z.infer<typeof CartSchema>

// «Скидка OSMIX»: промокоды площадки (управляются из админки).

export const PromoCodeSchema = z.object({
  code: z.string(),
  kind: PromoKindSchema,
  /** percent — доля (0.10 = 10 %); fixed — сумма в рублях. */
  value: z.number().nonnegative(),
  minSubtotal: z.number().nonnegative().default(0),
  /** Общий лимит выдач; null — без лимита. */
  maxUses: z.number().int().nonnegative().nullable().default(null),
  /** Лимит на аккаунт; null — без лимита, 1 — один раз на пользователя. */
  perUserLimit: z.number().int().min(1).nullable().default(1),
  usedCount: z.number().int().nonnegative().default(0),
  active: z.boolean().default(true),
  description: z.string().default(""),
  startsAt: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
  createdAt: z.string(),
  // ── Партнёрские промокоды (26.09.2026), аддитивно ──
  /** buyer — вводит покупатель при оформлении; seller — продавец при регистрации. */
  audience: PromoAudienceSchema.default("buyer"),
  partnerId: z.string().nullable().default(null),
  partner: PartnerRefSchema.nullable().default(null),
  /** Вознаграждение партнёра: percent — доля (0.05 = 5 %); фиксированных сумм нет. */
  rewardKind: PromoRewardKindSchema.default("none"),
  rewardValue: z.number().nonnegative().default(0),
  /** Только первый заказ покупателя на площадке. */
  firstOrderOnly: z.boolean().default(false),
  /** Только для тех, кто не покупал в магазине кода (shopId или shopIds), 27.10.2026. */
  newToShopOnly: z.boolean().default(false),
  /** Ограничение по магазинам/категориям; пусто — без ограничения. */
  shopIds: z.array(z.string()).default([]),
  categoryIds: z.array(z.string()).default([]),
  state: PromoStateSchema.default("active"),
  /** Код в архиве: не применяется, статистика сохранена. */
  archivedAt: z.string().nullable().default(null),
  /** Ключевые цифры за всё время (есть в списке консоли). */
  summary: PromoSummarySchema.optional(),
  /** Партнёрские ссылки: витрина (?promo=) и регистрация продавца. */
  links: z.object({ storefront: z.string(), sellerRegister: z.string() }).optional(),
  // ── Механики П2 (миграции 320, 323), аддитивно ──
  /** Код магазина (fd-promo-03): скидка за счёт магазина. null — код площадки. */
  shopId: z.string().nullable().default(null),
  shopName: z.string().nullable().default(null),
  /** Ограничение по товарам; пусто — без ограничения. */
  productIds: z.array(z.string()).default([]),
  /** Персональный код (fd-promo-12): применяет только этот покупатель. */
  userId: z.string().nullable().default(null),
  /** Виден покупателям в «Мои купоны» (fd-promo-34). */
  isPublic: z.boolean().default(false),
  /** Восстанавливать код при полной отмене заказа (fd-promo-19). */
  restoreOnCancel: z.boolean().default(true),
  /** Правило, выдавшее персональный код. */
  personalRule: z.string().nullable().default(null),
  /** Имя владельца персонального кода (только во вкладке «Персональные»). */
  userName: z.string().nullable().optional(),
})
export type PromoCode = z.infer<typeof PromoCodeSchema>

// Ввод/правка промокода из админки. code required только на создании.
export const PromoCodeInputSchema = z.object({
  code: z.string().trim().min(1).max(64),
  kind: PromoKindSchema,
  value: z.number().nonnegative(),
  minSubtotal: z.number().nonnegative().default(0),
  maxUses: z.number().int().nonnegative().nullable().default(null),
  perUserLimit: z.number().int().min(1).nullable().default(1),
  active: z.boolean().default(true),
  description: z.string().default(""),
  startsAt: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
  audience: PromoAudienceSchema.default("buyer"),
  partnerId: z.string().nullable().default(null),
  rewardKind: PromoRewardKindSchema.default("none"),
  /** Доля: 0.05 — это 5 %. */
  rewardValue: z.number().nonnegative().default(0),
  firstOrderOnly: z.boolean().default(false),
  shopIds: z.array(z.string()).max(200).default([]),
  categoryIds: z.array(z.string()).max(200).default([]),
  // П2, необязательные: старые клиенты консоли их не присылают.
  productIds: z.array(z.string()).max(500).optional(),
  isPublic: z.boolean().optional(),
  restoreOnCancel: z.boolean().optional(),
  /** Только для тех, кто не покупал в выбранных магазинах (27.10.2026). */
  newToShopOnly: z.boolean().optional(),
})
export type PromoCodeInput = z.infer<typeof PromoCodeInputSchema>

// Частичная правка: code менять нельзя (первичный ключ), остальное опционально.
export const PromoCodePatchSchema = PromoCodeInputSchema.omit({ code: true })
  .partial()
  // Архив вместо удаления использованного кода (true — в архив, false — вернуть).
  .extend({ archived: z.boolean().optional() })
export type PromoCodePatch = z.infer<typeof PromoCodePatchSchema>

export const AddCartItemRequestSchema = z.object({
  productId: z.string(),
  variantId: z.string().optional(),
  size: z.string().optional().default(""),
  color: z.string().optional().default(""),
  qty: z.number().int().positive().optional().default(1),
})
export type AddCartItemRequest = z.infer<typeof AddCartItemRequestSchema>

export const ContactInfoSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string(),
})
export type ContactInfo = z.infer<typeof ContactInfoSchema>

export const ShippingAddressSchema = z.object({
  country: z.string(),
  city: z.string(),
  street: z.string(),
  postalCode: z.string(),
  comment: z.string().optional(),
  /** Субъект РФ из справочника (fd-analytics-04); у старых заказов может не быть. */
  region: z.string().optional(),
  /** ФИАС-код субъекта из подсказки DaData. */
  regionFiasId: z.string().optional(),
})
export type ShippingAddress = z.infer<typeof ShippingAddressSchema>

export const DeliveryOptionSchema = z.object({
  carrier: DeliveryCarrierSchema,
  method: DeliveryMethodSchema.optional(),
  tariffCode: z.number().optional(),
  label: z.string(),
  deliverySum: z.number(),
  deliveryDays: z.string().optional(),
  toPickupPoint: z.boolean().optional(),
  /** СДЭК примерка (TRYING_ON) доступна для этой опции — доставка в ПВЗ, где
   *  возможна примерочная. Итоговая доступность зависит от конкретного ПВЗ
   *  (см. isDressingRoom у пункта выдачи). */
  supportsTryOn: z.boolean().optional(),
  /** СДЭК частичный выкуп (PART_DELIV) доступен для этой опции. */
  supportsPartial: z.boolean().optional(),
  /** §8: опция недоступна для адреса (например, курьер вне зоны доставки). Не
   *  исчезает из списка — показывается отключённой с пояснением. */
  unavailable: z.boolean().optional(),
  /** Причина недоступности (для показа рядом с отключённой опцией). */
  unavailableReason: z.string().optional(),
})
export type DeliveryOption = z.infer<typeof DeliveryOptionSchema>

/** Пункт выдачи (ПВЗ/постамат) для чекаута. `isDressingRoom` — есть ли
 *  примерочная (условие примерки и частичного выкупа). */
export const DeliveryPointSchema = z.object({
  code: z.string(),
  name: z.string(),
  address: z.string(),
  cityCode: z.number(),
  city: z.string(),
  longitude: z.number(),
  latitude: z.number(),
  type: z.string(),
  workTime: z.string(),
  isDressingRoom: z.boolean(),
  /** Перевозчик, чей это пункт выдачи. Аддитивно: у старых снапшотов ответа
   *  поля нет. Витрина подписывает пин и шторку брендом, а не разбором `name`. */
  carrier: DeliveryCarrierSchema.optional(),
})
export type DeliveryPoint = z.infer<typeof DeliveryPointSchema>

export const DeliveryQuoteGroupSchema = z.object({
  shopId: z.string(),
  shopName: z.string(),
  carrier: DeliveryCarrierSchema,
  options: z.array(DeliveryOptionSchema),
  /** §8: доступна ли курьерская доставка «до двери» для адреса (зона курьера).
   *  Отсутствует у старых снапшотов ответа. */
  courierAvailable: z.boolean().optional(),
  /** Причина недоступности курьера (например, «Курьер не доставляет в этот город»). */
  courierUnavailableReason: z.string().optional(),
})
export type DeliveryQuoteGroup = z.infer<typeof DeliveryQuoteGroupSchema>

export const DeliveryQuoteSchema = z.object({
  groups: z.array(DeliveryQuoteGroupSchema),
})
export type DeliveryQuote = z.infer<typeof DeliveryQuoteSchema>

export const DeliveryQuoteRequestSchema = z.object({
  cartId: z.string(),
  country: z.string().optional(),
  city: z.string(),
  postalCode: z.string().optional(),
  /** Частичное оформление: только выбранные строки корзины (как lineIds заказа). */
  lineIds: z.array(z.string()).optional(),
})
export type DeliveryQuoteRequest = z.infer<typeof DeliveryQuoteRequestSchema>

export const DeliverySelectionSchema = z.object({
  shopId: z.string(),
  carrier: DeliveryCarrierSchema,
  method: DeliveryMethodSchema.optional(),
  tariffCode: z.number().optional(),
  tariffName: z.string().optional(),
  deliveryDays: z.string().optional(),
  deliverySum: z.number(),
  toPickupPoint: z.boolean().optional(),
  pvzCode: z.string().optional(),
  pvzAddress: z.string().optional(),
  /** Время работы выбранного ПВЗ. Аддитивно: у старых снапшотов заказа поля нет. */
  pvzWorkTime: z.string().optional(),
  /** Покупатель заказал примерку (СДЭК TRYING_ON) в ПВЗ. */
  tryOn: z.boolean().optional(),
  /** Покупатель заказал частичный выкуп (СДЭК PART_DELIV). */
  partial: z.boolean().optional(),
  /** Самовывоз от продавца (carrier = seller_pickup): выбранная точка, обязательна. */
  pickupPointId: z.string().optional(),
})
export type DeliverySelection = z.infer<typeof DeliverySelectionSchema>

export const DeliveryConfigSchema = z.object({
  fees: z.object({
    standard: z.number(),
    express: z.number(),
    pickup: z.number(),
  }),
  freeOver: z.number(),
  updatedAt: z.string(),
})
export type DeliveryConfig = z.infer<typeof DeliveryConfigSchema>

export const DeliveryConfigPatchSchema = z
  .object({
    fees: z
      .object({
        standard: z.number().min(0),
        express: z.number().min(0),
        pickup: z.number().min(0),
      })
      .partial()
      .optional(),
    freeOver: z.number().min(0).optional(),
  })
  .refine((patch) => patch.fees !== undefined || patch.freeOver !== undefined, {
    message: "Nothing to update",
  })
export type DeliveryConfigPatch = z.infer<typeof DeliveryConfigPatchSchema>

export const PaymentRequestSchema = z.object({
  cardNumber: z.string(),
  cardName: z.string(),
  expiry: z.string(),
  cvc: z.string(),
})
export type PaymentRequest = z.infer<typeof PaymentRequestSchema>

export const PaymentResultSchema = z.object({
  /** Optional for snapshots written before provider split. */
  provider: z.enum(["cloudpayments", "mock"]).optional(),
  status: z.enum(["pending", "approved", "declined"]),
  transactionId: z.string().optional(),
  brand: z.string(),
  last4: z.string(),
})
export type PaymentResult = z.infer<typeof PaymentResultSchema>

export const PublicPaymentConfigSchema = z.object({
  provider: z.enum(["cloudpayments", "mock"]),
  publicId: z.string().optional(),
  currency: z.string(),
  /** fd-payment-04: ставка комиссии площадки для ОТОБРАЖЕНИЯ строки комиссии на
   *  витрине. Не влияет на суммы заказа — комиссию несёт продавец. */
  commissionRate: z.number().optional(),
})
export type PublicPaymentConfig = z.infer<typeof PublicPaymentConfigSchema>

/** Способ оплаты заказа: предоплата (по умолчанию) или оплата при получении. */
export const PaymentModeSchema = z.enum(["prepaid", "cod"])
export type PaymentMode = z.infer<typeof PaymentModeSchema>

export const CreateOrderRequestPaymentSchema = z.object({
  /** fd-payment-03: оплатить (частично/полностью) внутренним балансом. */
  useBalance: z.boolean().optional(),
  balanceAmount: z.number().min(0).optional(),
  /** fd-payment-02: оплата по счёту для ЮЛ. */
  paymentMethod: z.enum(["invoice"]).optional(),
  /** Оплата при получении (СДЭК наложенный платёж). По умолчанию prepaid. */
  paymentMode: PaymentModeSchema.optional(),
})
export type CreateOrderRequestPayment = z.infer<typeof CreateOrderRequestPaymentSchema>

export const OrderItemSchema = z.object({
  productId: z.string(),
  name: z.string(),
  brand: z.string(),
  photo: z.string(),
  variantId: z.string().optional(),
  variantLabel: z.string().optional(),
  options: z.record(z.string(), z.string()).optional(),
  size: z.string(),
  color: z.string(),
  qty: z.number().int(),
  unitPrice: z.number(),
  oldUnitPrice: z.number().optional(),
  lineTotal: z.number(),
  weightGrams: z.number().int().positive().optional(),
  lengthCm: z.number().int().positive().optional(),
  widthCm: z.number().int().positive().optional(),
  heightCm: z.number().int().positive().optional(),
  // ── §2 Позиция заказа для мобильного макета (все поля опциональны/аддитивны) ──
  /** Короткое описание позиции (например, описание кастом-оффера). */
  description: z.string().optional(),
  /** Тип позиции: обычный товар / изготовление на заказ / индивидуальный заказ. */
  kind: z.enum(["regular", "madeToOrder", "customized"]).optional(),
  /** Характеристики позиции для карточки заказа. */
  specs: z.array(z.object({ label: z.string(), value: z.string(), imageUrl: z.string().optional() })).optional(),
  /** Ссылка «показать все характеристики». */
  specsMoreUrl: z.string().optional(),
  /** Оценка покупателя по этому товару (если оставлена). */
  rating: z.number().min(0).max(5).optional(),
  /** Можно ли оценить товар из этого заказа (доставлен/завершён и без оценки). */
  canRate: z.boolean().optional(),
  // ── Механики П2, аддитивно ──
  /** Доля скидки по промокоду на всю строку, рубли — записана при создании заказа. */
  discount: z.number().optional(),
  promoApplied: z.boolean().optional(),
  /** Акция магазина, по которой продана позиция (цена уже со скидкой). */
  sellerPromo: z.object({ id: z.string(), title: z.string() }).optional(),
})
export type OrderItem = z.infer<typeof OrderItemSchema>

export const OrderGroupShipmentSchema = z.object({
  carrier: DeliveryCarrierSchema,
  method: DeliveryMethodSchema.optional(),
  tariffCode: z.number().optional(),
  tariffName: z.string().optional(),
  deliveryDays: z.string().optional(),
  toPickupPoint: z.boolean().optional(),
  pvzCode: z.string().optional(),
  pvzAddress: z.string().optional(),
  /** Время работы ПВЗ. Аддитивно: у старых снапшотов заказа поля нет. */
  pvzWorkTime: z.string().optional(),
  /** Примерка в ПВЗ (СДЭК TRYING_ON). */
  tryOn: z.boolean().optional(),
  /** Частичный выкуп (СДЭК PART_DELIV). */
  partial: z.boolean().optional(),
  /** Самовывоз от продавца: снимок точки выдачи на момент оформления. */
  pickupPoint: z
    .object({
      id: z.string(),
      name: z.string(),
      city: z.string(),
      address: z.string(),
      postalCode: z.string().optional(),
      schedule: z.string(),
      phone: z.string(),
      instructions: z.string(),
      readyAmount: z.number(),
      readyUnit: z.enum(["hours", "days"]),
      readyLabel: z.string(),
      price: z.number(),
      storageDays: z.number(),
    })
    .optional(),
})
export type OrderGroupShipment = z.infer<typeof OrderGroupShipmentSchema>

/**
 * Самовывоз от продавца глазами покупателя: статус выдачи, срок хранения, код.
 * Поле `sellerPickup` группы заказа есть только в покупательских ответах
 * (/buyer/orders…), поэтому в общую схему группы (её видит и партнёрский API
 * продавцов) оно не входит.
 */
export const OrderGroupSellerPickupSchema = z.object({
  status: z.enum(["new", "ready", "issued", "expired"]),
  readyAt: z.string().optional(),
  storageUntil: z.string().optional(),
  handedAt: z.string().optional(),
  code: z.string().optional(),
})
export type OrderGroupSellerPickup = z.infer<typeof OrderGroupSellerPickupSchema>

export const OrderGroupSchema = z.object({
  shopId: z.string(),
  shopName: z.string(),
  shopSlug: z.string(),
  items: z.array(OrderItemSchema),
  subtotal: z.number(),
  deliveryFee: z.number(),
  shipment: OrderGroupShipmentSchema.optional(),
  /**
   * Деньги продавца по этому заказу: товарная часть минус комиссия площадки.
   * Доставка сюда не входит — она транзитная и продавцу не принадлежит.
   * Заполняется только в срезе кабинета продавца: покупателю эти числа не видны.
   */
  payout: z
    .object({
      commissionRate: z.number(),
      commission: z.number(),
      /** Начислено продавцу: subtotal − комиссия. */
      accrued: z.number(),
    })
    .optional(),
  // ── Механики П2, аддитивно ──
  /** Скидка по промокоду в группе (сумма долей позиций). */
  discount: z.number().optional(),
  /** Часть скидки за счёт магазина (код магазина) — уменьшает его начисление. */
  shopDiscount: z.number().optional(),
  /** Доставка, обнулённая кодом на бесплатную доставку; перевозчику платит площадка. */
  deliveryDiscount: z.number().optional(),
})
export type OrderGroup = z.infer<typeof OrderGroupSchema>

export const OrderSchema = z.object({
  id: z.string(),
  status: OrderStatusSchema,
  contact: ContactInfoSchema,
  address: ShippingAddressSchema,
  delivery: DeliveryMethodSchema,
  payment: PaymentResultSchema,
  groups: z.array(OrderGroupSchema),
  subtotal: z.number(),
  /** «Скидка OSMIX» в рублях, вычтенная из subtotal (0 — промо не применено). */
  discount: z.number().default(0),
  /** Применённый промокод (для журнала погашений). */
  promoCode: z.string().optional(),
  promoKind: PromoKindSchema.optional(),
  /** Код магазина: id магазина, за чей счёт скидка. Нет — код площадки. */
  promoShopId: z.string().optional(),
  /** Обнулённая доставка по коду (сумма по группам). */
  deliveryDiscount: z.number().optional(),
  /** Раздельное оформление (fd-promo-16): общий id оформления и заказы в нём. */
  checkoutId: z.string().optional(),
  checkoutOrders: z
    .array(z.object({ id: z.string(), number: z.number().optional(), total: z.number(), promo: z.boolean() }))
    .optional(),
  /** Частичное оформление: строки корзины, из которых собран заказ (при оплате
   *  из корзины удаляются только они). Без поля — вся корзина. */
  cartLineIds: z.array(z.string()).optional(),
  deliveryTotal: z.number(),
  total: z.number(),
  itemCount: z.number().int(),
  /** Optional for order snapshots written before payment-currency capture. */
  currency: z.string().optional(),
  /** fd-payment-03: сумма, оплаченная внутренним балансом (вне PSP-платежа). */
  balanceApplied: z.number().optional(),
  /** fd-payment-03: сумма к оплате провайдером (total − balanceApplied). */
  payableTotal: z.number().optional(),
  /** fd-payment-02: 'invoice' — заказ на оплату по счёту (ЮЛ). */
  paymentMethod: z.enum(["invoice"]).optional(),
  /** Оплата при получении: 'cod' — деньги собирает СДЭК при выдаче, площадка
   *  не списывает их при оформлении. Отсутствует/'prepaid' у обычных заказов. */
  paymentMode: PaymentModeSchema.optional(),
  /** Set once when verified payment moves a pending order to a paid state. */
  paidAt: z.string().optional(),
  createdAt: z.string(),
  // ── §2 Поля карточки/списка заказов мобильного макета (аддитивны) ──
  /** Заказ в архиве (завершённые/скрытые) — для вкладки «Возврат» списка. */
  archived: z.boolean().optional(),
  /** Ведро списка: активные vs завершённые. */
  bucket: z.enum(["active", "completed"]).optional(),
  /** Код получения в ПВЗ (генерируется детерминированно из id заказа). */
  pickupCode: z.string().optional(),
  /** Штрих-/QR-код кода получения. */
  pickupCodeBarcode: z
    .object({ format: z.enum(["qr", "code128"]), payload: z.string() })
    .optional(),
  /** Трекинг доставки (из отгрузки/таймлайна, best-effort). */
  deliveryTracking: z
    .object({
      statusKey: z.string(),
      etaText: z.string().optional(),
      events: z.array(
        z.object({
          at: z.string().optional(),
          statusKey: z.string().optional(),
          label: z.string().optional(),
        }),
      ),
    })
    .optional(),
})
export type Order = z.infer<typeof OrderSchema>

export const ShipmentStatusHistoryEntrySchema = z.object({
  code: z.string().optional(),
  name: z.string().optional(),
  at: z.string().optional(),
  /** Комментарий продавца к ручному статусу своей доставки. */
  comment: z.string().optional(),
})
export type ShipmentStatusHistoryEntry = z.infer<typeof ShipmentStatusHistoryEntrySchema>

export const OrderShipmentSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  shopId: z.string(),
  carrier: DeliveryCarrierSchema,
  sdekUuid: z.string().optional(),
  cdekNumber: z.string().optional(),
  tariffCode: z.number().optional(),
  deliveryPoint: z.string().optional(),
  deliverySum: z.number().optional(),
  /** Хэндл у перевозчика не-СДЭК (код груза ПЭК, заявка Деловых линий). */
  carrierRef: z.string().optional(),
  trackingNumber: z.string().optional(),
  state: z.string(),
  statusCode: z.string().optional(),
  statusName: z.string().optional(),
  statusAt: z.string().optional(),
  statusHistory: z.array(ShipmentStatusHistoryEntrySchema),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type OrderShipment = z.infer<typeof OrderShipmentSchema>

export const CreateOrderRequestSchema = z.object({
  cartId: z.string(),
  contact: ContactInfoSchema,
  address: ShippingAddressSchema,
  /** One delivery selection per shop group in the cart. */
  selections: z.array(DeliverySelectionSchema),
  /** fd-payment-03: оплатить (частично/полностью) внутренним балансом. */
  useBalance: z.boolean().optional(),
  balanceAmount: z.number().min(0).optional(),
  /** fd-payment-02: 'invoice' — оформить заказ на оплату по счёту (ЮЛ). */
  paymentMethod: z.enum(["invoice"]).optional(),
  /** Оплата при получении (СДЭК наложенный платёж). По умолчанию prepaid. */
  paymentMode: PaymentModeSchema.optional(),
  /** §8 Частичный чекаут: оформить заказ только по выбранным строкам корзины
   *  (id из cart.items[].id). Без этого поля — вся корзина, как раньше.
   *  `itemIds` — синоним `lineIds` (принимаем оба). */
  lineIds: z.array(z.string()).optional(),
  itemIds: z.array(z.string()).optional(),
  /** fd-promo-16: товары со скидкой по промокоду — отдельным заказом. */
  splitPromo: z.boolean().optional(),
  /** Цифровой UGC-артикул из памяти устройства (30 дней, ugc-articles.ts).
   *  Необязательно: без него шлюз ищет переход по артикулу сам. */
  ugcArticleCode: z.string().trim().max(16).optional(),
})
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>
